import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiRouteConstants } from '../../constants/api-routes';
import { PublicAppConfiguration } from './models/public-app-configuration';

@Injectable({
    providedIn: 'root',
})
export class PublicAppConfigurationService {
    private _publicAppConfiguration$ =
        new BehaviorSubject<PublicAppConfiguration | null>(null);

    public readonly publicAppConfiguration$: Observable<PublicAppConfiguration | null> =
        this._publicAppConfiguration$.asObservable();

    constructor(private readonly http: HttpClient) {}

    /**
     * Fetches the public app configuration from the API.
     * @returns An observable that emits the public app configuration.
     */
    getPublicAppConfiguration(): Observable<PublicAppConfiguration> {
        return this.http.get<PublicAppConfiguration>(
            ApiRouteConstants.PublicAppConfigurationApiRoutes.rootUrl,
        );
    }

    /**
     * Fetches the application configuration and sets it as the current application configuration.
     * @returns An observable that emits the application configuration.
     */
    refreshPublicAppConfiguration(): Observable<PublicAppConfiguration> {
        return this.getPublicAppConfiguration().pipe(
            tap((publicAppConfiguration: PublicAppConfiguration) =>
                this._publicAppConfiguration$.next(publicAppConfiguration),
            ),
        );
    }

    /**
     * Retrieves the current public app configuration or refreshes it if not available.
     */
    getOrRefreshApplicationConfiguration(): Observable<PublicAppConfiguration> {
        return this._publicAppConfiguration$.pipe(
            switchMap(
                (publicAppConfiguration: PublicAppConfiguration | null) => {
                    if (publicAppConfiguration) {
                        return of(publicAppConfiguration);
                    } else {
                        return this.refreshPublicAppConfiguration();
                    }
                },
            ),
        );
    }
}
